import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield,
  Plus,
  Edit3,
  Trash2,
  X,
  DollarSign,
  Building,
  Calendar,
  Search,
  RefreshCw,
  CheckCircle,
  Clock,
  MinusCircle,
  ChevronDown,
  AlertTriangle,
} from 'lucide-react';
import { depositsAPI, roomsAPI } from '../services/api';
import { formatCurrency, formatDate } from '../utils/helpers';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import toast from 'react-hot-toast';

const STATUS_CONFIG = {
  held:               { label: 'Held',               color: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',   icon: Clock },
  partially_refunded: { label: 'Partial Refund',      color: 'bg-amber-500/20 text-amber-400 border border-amber-500/30', icon: MinusCircle },
  refunded:           { label: 'Fully Refunded',      color: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30', icon: CheckCircle },
};

const EMPTY_FORM = {
  room_id: '',
  tenant_name: '',
  deposit_amount: '',
  deposit_date: '',
  status: 'held',
  refunded_amount: '',
  refund_date: '',
  deductions: '',
  deduction_notes: '',
  notes: '',
};

const DepositsPage = () => {
  const [deposits,   setDeposits]   = useState([]);
  const [rooms,      setRooms]      = useState([]);
  const [summary,    setSummary]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal,  setShowModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [depRes, roomRes, sumRes] = await Promise.all([
        depositsAPI.getAll(),
        roomsAPI.getAll(),
        depositsAPI.getSummary(),
      ]);
      setDeposits(depRes.data.deposits || depRes.data || []);
      setRooms(roomRes.data.rooms || roomRes.data || []);
      setSummary(sumRes.data || null);
    } catch (err) {
      toast.error('Failed to load deposits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (dep) => {
    setEditing(dep);
    setForm({
      room_id:         dep.room_id   ?? '',
      tenant_name:     dep.tenant_name ?? '',
      deposit_amount:  dep.deposit_amount ?? '',
      deposit_date:    dep.deposit_date ? dep.deposit_date.slice(0, 10) : '',
      status:          dep.status ?? 'held',
      refunded_amount: dep.refunded_amount ?? '',
      refund_date:     dep.refund_date ? dep.refund_date.slice(0, 10) : '',
      deductions:      dep.deductions ?? '',
      deduction_notes: dep.deduction_notes ?? '',
      notes:           dep.notes ?? '',
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.room_id || !form.tenant_name || !form.deposit_amount || !form.deposit_date) {
      toast.error('Room, tenant name, amount and date are required');
      return;
    }
    try {
      setSaving(true);
      if (editing) {
        await depositsAPI.update(editing.id, form);
        toast.success('Deposit updated');
      } else {
        await depositsAPI.create(form);
        toast.success('Deposit recorded');
      }
      setShowModal(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await depositsAPI.delete(deleteTarget.id);
      toast.success('Deposit deleted');
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const filtered = deposits.filter(d => {
    const matchSearch = !search ||
      d.tenant_name?.toLowerCase().includes(search.toLowerCase()) ||
      d.room_number?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || d.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const summaryCards = summary ? [
    { label: 'Deposits Held',    value: formatCurrency(summary.total_held),       sub: `${summary.held_count} active`,         icon: Shield,       color: 'text-blue-400',    bg: 'bg-blue-500/10' },
    { label: 'Total Deductions', value: formatCurrency(summary.total_deductions),  sub: `${summary.partial_count} partial`,      icon: MinusCircle,  color: 'text-amber-400',   bg: 'bg-amber-500/10' },
    { label: 'Refunded',         value: formatCurrency(summary.total_refunded),    sub: `${summary.refunded_count} closed`,      icon: CheckCircle,  color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    { label: 'Net Balance',      value: formatCurrency(summary.net_balance),       sub: `${summary.total_count} total records`,  icon: DollarSign,   color: 'text-primary-400', bg: 'bg-primary-500/10' },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary-500" />
            Security Deposits
          </h1>
          <p className="text-secondary-500 dark:text-secondary-400 text-sm mt-1">
            Track deposit holdings, deductions and refunds per room
          </p>
        </div>
        <button onClick={openAdd} className="btn-primary flex items-center gap-2 self-start sm:self-auto">
          <Plus className="w-4 h-4" />
          New Deposit
        </button>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {summaryCards.map((card, i) => (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="card"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">{card.label}</p>
                  <p className={`text-2xl font-bold mt-1 ${card.color}`}>{card.value}</p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">{card.sub}</p>
                </div>
                <div className={`w-10 h-10 rounded-xl ${card.bg} flex items-center justify-center`}>
                  <card.icon className={`w-5 h-5 ${card.color}`} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
            <input
              type="text"
              placeholder="Search tenant or room…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="input-field pl-9 w-full"
            />
          </div>
          <div className="relative">
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="input-field pr-8 appearance-none"
            >
              <option value="">All statuses</option>
              <option value="held">Held</option>
              <option value="partially_refunded">Partial Refund</option>
              <option value="refunded">Fully Refunded</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
          </div>
          <button onClick={fetchAll} className="btn-secondary flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="loading-spinner" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-secondary-400">
            <Shield className="w-10 h-10 mb-3 opacity-30" />
            <p>No deposits found</p>
            <button onClick={openAdd} className="btn-primary mt-4 text-sm">Record First Deposit</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-secondary-200 dark:border-secondary-700">
                  {['Room', 'Tenant', 'Amount', 'Date', 'Deductions', 'Refunded', 'Balance', 'Status', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((dep, idx) => {
                  const balance = parseFloat(dep.deposit_amount || 0) - parseFloat(dep.deductions || 0) - parseFloat(dep.refunded_amount || 0);
                  const cfg = STATUS_CONFIG[dep.status] || STATUS_CONFIG.held;
                  return (
                    <motion.tr
                      key={dep.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.02 }}
                      className="border-b border-secondary-100 dark:border-secondary-700/50 hover:bg-secondary-50 dark:hover:bg-secondary-700/30 transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-secondary-900 dark:text-secondary-100">
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4 text-secondary-400" />
                          {dep.room_number || `#${dep.room_id}`}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-secondary-700 dark:text-secondary-300">{dep.tenant_name}</td>
                      <td className="px-4 py-3 text-emerald-600 dark:text-emerald-400 font-semibold">{formatCurrency(dep.deposit_amount)}</td>
                      <td className="px-4 py-3 text-secondary-600 dark:text-secondary-400 text-sm">{formatDate(dep.deposit_date)}</td>
                      <td className="px-4 py-3 text-amber-600 dark:text-amber-400">{formatCurrency(dep.deductions || 0)}</td>
                      <td className="px-4 py-3 text-secondary-600 dark:text-secondary-400">{formatCurrency(dep.refunded_amount || 0)}</td>
                      <td className={`px-4 py-3 font-bold ${balance > 0 ? 'text-blue-500' : 'text-secondary-400'}`}>{formatCurrency(balance)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                          <cfg.icon className="w-3 h-3" />
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => openEdit(dep)} className="p-1.5 rounded-md hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors text-secondary-500 dark:text-secondary-400 hover:text-primary-500">
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button onClick={() => setDeleteTarget(dep)} className="p-1.5 rounded-md hover:bg-red-500/10 transition-colors text-secondary-500 dark:text-secondary-400 hover:text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-secondary-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between p-6 border-b border-secondary-200 dark:border-secondary-700">
                <h2 className="text-lg font-bold text-secondary-900 dark:text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary-500" />
                  {editing ? 'Edit Deposit' : 'New Security Deposit'}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors">
                  <X className="w-5 h-5 text-secondary-500" />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Room */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Room *</label>
                    <div className="relative">
                      <select
                        value={form.room_id}
                        onChange={e => setForm(f => ({ ...f, room_id: e.target.value }))}
                        className="input-field w-full appearance-none pr-8"
                        required
                      >
                        <option value="">Select room…</option>
                        {rooms.map(r => (
                          <option key={r.id} value={r.id}>Room {r.room_number}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Tenant name */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Tenant Name *</label>
                    <input
                      type="text"
                      value={form.tenant_name}
                      onChange={e => setForm(f => ({ ...f, tenant_name: e.target.value }))}
                      className="input-field w-full"
                      placeholder="Enter tenant name"
                      required
                    />
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Deposit Amount (£) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.deposit_amount}
                      onChange={e => setForm(f => ({ ...f, deposit_amount: e.target.value }))}
                      className="input-field w-full"
                      placeholder="0.00"
                      required
                    />
                  </div>

                  {/* Date */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Deposit Date *</label>
                    <input
                      type="date"
                      value={form.deposit_date}
                      onChange={e => setForm(f => ({ ...f, deposit_date: e.target.value }))}
                      className="input-field w-full"
                      required
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Status</label>
                    <div className="relative">
                      <select
                        value={form.status}
                        onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                        className="input-field w-full appearance-none pr-8"
                      >
                        <option value="held">Held</option>
                        <option value="partially_refunded">Partially Refunded</option>
                        <option value="refunded">Fully Refunded</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Deductions */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Deductions (£)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.deductions}
                      onChange={e => setForm(f => ({ ...f, deductions: e.target.value }))}
                      className="input-field w-full"
                      placeholder="0.00"
                    />
                  </div>

                  {/* Refunded amount */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Refunded Amount (£)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.refunded_amount}
                      onChange={e => setForm(f => ({ ...f, refunded_amount: e.target.value }))}
                      className="input-field w-full"
                      placeholder="0.00"
                    />
                  </div>

                  {/* Refund date */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Refund Date</label>
                    <input
                      type="date"
                      value={form.refund_date}
                      onChange={e => setForm(f => ({ ...f, refund_date: e.target.value }))}
                      className="input-field w-full"
                    />
                  </div>
                </div>

                {/* Deduction notes */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Deduction Notes</label>
                  <textarea
                    rows={2}
                    value={form.deduction_notes}
                    onChange={e => setForm(f => ({ ...f, deduction_notes: e.target.value }))}
                    className="input-field w-full resize-none"
                    placeholder="Reason for deductions…"
                  />
                </div>

                {/* General notes */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Notes</label>
                  <textarea
                    rows={2}
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    className="input-field w-full resize-none"
                    placeholder="Additional notes…"
                  />
                </div>

                {/* Balance preview */}
                {form.deposit_amount && (
                  <div className="rounded-xl bg-primary-500/10 border border-primary-500/20 p-4">
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">Projected Balance</p>
                    <p className="text-2xl font-bold text-primary-500 mt-1">
                      {formatCurrency(
                        Math.max(0,
                          parseFloat(form.deposit_amount || 0) -
                          parseFloat(form.deductions || 0) -
                          parseFloat(form.refunded_amount || 0)
                        )
                      )}
                    </p>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button>
                  <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
                    {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                    {editing ? 'Update Deposit' : 'Record Deposit'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete modal */}
      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        itemName={deleteTarget ? `${deleteTarget.tenant_name}'s deposit` : ''}
        itemType="deposit"
      />
    </div>
  );
};

export default DepositsPage;
